import { Box, Divider, FormField, ImageViewer, Input, SidePanel } from "@wix/design-system";
import classNames from "classnames";
import React, { useContext, useRef } from "react";
import {
  ModalServiceContext,
  PluginsEventsContext,
  RicosContext,
  ZIndexContext
} from "./../../ricos-context";
import { getImageSrc } from "./../../wix-rich-content-common/lib/imageUtils";
import {
  ToggleButton,
  UiComponentsProvider,
  useClickOutside
} from "./../../ui-components";
import { useBiNext } from "../../_shared/use-bi-next";
import { useNodeAttrs } from "../../_shared/use-node-attrs";
import { AUDIO_ACTION_NAMES, AUDIO_BI_VALUES, AUDIO_TYPES, audioModals } from "../consts";
import {
  AUDIO_COVER_IMAGE,
  AUDIO_DOWNLOAD_TOGGLE,
  AUDIO_SETTINGS_AUDIO_NAME_INPUT,
  AUDIO_SETTINGS_AUTHOR_NAME_INPUT,
  AUDIO_SETTINGS_MODAL
} from "../dataHooks";
import classes from "../statics/styles/settings-modal-scss";
import { AUDIO_TYPE } from "../types";
const AudioSettingsModal = ({ nodeId, onFileSelect }) => {
  const { t, environment, isMobile } = useContext(RicosContext);
  const modalService = useContext(ModalServiceContext) || {};
  const pluginsEvents = useContext(PluginsEventsContext);
  const zIndexService = useContext(ZIndexContext);
  const modalRef = useRef(null);
  const tooltipRef = useRef(null);
  const { patchPmNodeAttrs, pmNodeAttrs } = useNodeAttrs(nodeId, "AUDIO");
  const logBi = useBiNext();
  useClickOutside({
    containerRef: modalRef,
    onClickOutside: (event) => {
      if (tooltipRef.current?.contains(event.target)) {
        return;
      }
      closeModal();
    },
    triggerOn: "pointerdown"
  });
  if (!pmNodeAttrs) {
    console.error("Audio settings: node attrs not found");
    return null;
  }
  const closeModal = () => {
    modalService.closeModal(audioModals.settings);
  };
  const imgSrc = pmNodeAttrs.coverImage && getImageSrc(
    { file_name: pmNodeAttrs.coverImage.src?.id || pmNodeAttrs.coverImage?.src.id },
    void 0,
    {
      requiredWidth: 120,
      requiredHeight: 120,
      requiredQuality: 70
    }
  );
  const onInputBlur = (actionName, value) => {
    pluginsEvents.publishPluginChangeSettings?.({
      nodeId,
      pluginId: AUDIO_TYPE,
      actionName,
      value
    });
  };
  return /* @__PURE__ */ React.createElement(UiComponentsProvider, null, /* @__PURE__ */ React.createElement("div", { tabIndex: -1, className: classNames(classes.modal), ref: modalRef }, /* @__PURE__ */ React.createElement(
    SidePanel,
    {
      onCloseButtonClick: closeModal,
      dataHook: AUDIO_SETTINGS_MODAL,
      skin: "floating",
      width: isMobile ? "100%" : 288,
      height: isMobile ? "100vh" : void 0
    },
    /* @__PURE__ */ React.createElement("div", { "data-drag-handler": "true" }, /* @__PURE__ */ React.createElement(
      SidePanel.Header,
      {
        showDivider: true,
        className: classes.header,
        title: t("AudioPlugin_Settings_Header")
      }
    )),
    /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement(Box, { direction: "vertical", padding: "SP3", gap: "SP3" }, /* @__PURE__ */ React.createElement(FormField, { label: t("AudioPlugin_Settings_AudioName_Label") }, /* @__PURE__ */ React.createElement(
      Input,
      {
        placeholder: t("AudioPlugin_Settings_AudioName_Label_Placeholder"),
        value: pmNodeAttrs.name ?? void 0,
        onBlur: (e) => {
          onInputBlur(AUDIO_ACTION_NAMES.changeTitle, e.target.value);
        },
        onChange: (e) => {
          patchPmNodeAttrs({
            name: e.target.value
          });
          logBi({ dataHook: AUDIO_SETTINGS_AUDIO_NAME_INPUT });
          pluginsEvents.publishMediaPluginsDetails?.({
            pluginId: AUDIO_TYPE,
            nodeId,
            creator: pmNodeAttrs.authorName ?? void 0,
            title: e.target.value,
            track_duration: pmNodeAttrs.audio?.duration?.toString(),
            type: AUDIO_TYPES.custom,
            url: pmNodeAttrs.audio?.src?.id ?? ""
          });
        },
        dataHook: AUDIO_SETTINGS_AUDIO_NAME_INPUT,
        size: "small"
      }
    )), /* @__PURE__ */ React.createElement(FormField, { label: t("AudioPlugin_Settings_AuthorName_Label") }, /* @__PURE__ */ React.createElement(
      Input,
      {
        placeholder: t("AudioPlugin_Settings_AuthorName_Label_Placeholder"),
        value: pmNodeAttrs.authorName ?? void 0,
        onBlur: (e) => {
          onInputBlur(AUDIO_ACTION_NAMES.changeCreator, e.target.value);
        },
        onChange: (e) => {
          patchPmNodeAttrs({ authorName: e.target.value });
          logBi({ dataHook: AUDIO_SETTINGS_AUTHOR_NAME_INPUT });
          pluginsEvents.publishMediaPluginsDetails?.({
            pluginId: AUDIO_TYPE,
            nodeId,
            creator: e.target.value,
            title: pmNodeAttrs.name ?? void 0,
            track_duration: pmNodeAttrs.audio?.duration?.toString(),
            type: AUDIO_TYPES.custom,
            url: pmNodeAttrs.audio?.src?.id ?? ""
          });
        },
        dataHook: AUDIO_SETTINGS_AUTHOR_NAME_INPUT,
        size: "small"
      }
    )), /* @__PURE__ */ React.createElement(FormField, { label: t("AudioPlugin_Settings_CoverImage_Label") }, /* @__PURE__ */ React.createElement(
      ImageViewer,
      {
        className: environment === "liveSite" ? classes.imageViewer : void 0,
        dataHook: AUDIO_COVER_IMAGE,
        imageUrl: imgSrc,
        width: 120,
        height: 120,
        removeRoundedBorders: environment === "liveSite",
        onAddImage: () => {
          pluginsEvents.publishPluginChangeSettings?.({
            pluginId: AUDIO_TYPE,
            nodeId,
            actionName: AUDIO_ACTION_NAMES.changeCover,
            value: AUDIO_BI_VALUES.YES
          });
          onFileSelect();
        },
        onUpdateImage: () => {
          pluginsEvents.publishPluginChangeSettings?.({
            pluginId: AUDIO_TYPE,
            nodeId,
            actionName: AUDIO_ACTION_NAMES.changeCover,
            value: AUDIO_BI_VALUES.YES
          });
          onFileSelect(0);
        },
        onRemoveImage: () => {
          patchPmNodeAttrs({ coverImage: null });
          logBi({ dataHook: AUDIO_COVER_IMAGE });
          pluginsEvents.publishMediaPluginsDetails?.({
            pluginId: AUDIO_TYPE,
            nodeId,
            creator: pmNodeAttrs.authorName ?? void 0,
            title: pmNodeAttrs.name ?? void 0,
            track_duration: pmNodeAttrs.audio?.duration?.toString(),
            type: AUDIO_TYPES.custom,
            url: pmNodeAttrs.audio?.src?.id ?? ""
          });
          pluginsEvents.publishPluginChangeSettings?.({
            nodeId,
            pluginId: AUDIO_TYPE,
            actionName: AUDIO_ACTION_NAMES.changeCover,
            value: AUDIO_BI_VALUES.NO
          });
        },
        tooltipProps: {
          appendTo: modalRef.current,
          zIndex: zIndexService.getZIndex("TOOLTIP")
        }
      }
    )), /* @__PURE__ */ React.createElement(Divider, null), /* @__PURE__ */ React.createElement(
      ToggleButton,
      {
        checked: !pmNodeAttrs.disableDownload,
        title: t("AudioPlugin_Settings_AudioCanBeDownloaded_Label"),
        size: "small",
        dataHook: AUDIO_DOWNLOAD_TOGGLE,
        onChange: () => {
          const disableDownload = !pmNodeAttrs.disableDownload;
          patchPmNodeAttrs({ disableDownload });
          logBi({ dataHook: AUDIO_DOWNLOAD_TOGGLE });
          pluginsEvents.publishPluginChangeSettings?.({
            pluginId: AUDIO_TYPE,
            nodeId,
            actionName: AUDIO_ACTION_NAMES.downloadAudio,
            value: disableDownload ? AUDIO_BI_VALUES.YES : AUDIO_BI_VALUES.NO
          });
          pluginsEvents.publishMediaPluginsDetails?.({
            pluginId: AUDIO_TYPE,
            nodeId,
            creator: pmNodeAttrs.authorName ?? void 0,
            title: pmNodeAttrs.name ?? void 0,
            track_duration: pmNodeAttrs.audio?.duration?.toString(),
            type: AUDIO_TYPES.custom,
            url: pmNodeAttrs.audio?.src?.id ?? ""
          });
        },
        infoContent: /* @__PURE__ */ React.createElement("div", { ref: tooltipRef }, t("AudioPlugin_Settings_AudioCanBeDownloaded_Tooltip"))
      }
    )))
  )));
};
export {
  AudioSettingsModal
};
//# sourceMappingURL=SettingsModal.js.map
